import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {useCountdown} from '../Countdown/useCountdown';
import {bannerWideAndUpLinaria} from './bannerContainer';

export interface BannerCountdownData {
  endsAt: Date;
}

interface BannerCountdownProps {
  endsAt: Date;
  /** Tint applied to clock icon + label. Defaults to black. */
  foregroundColor?: string;
  className?: string;
  ['data-testid']?: string;
  ['data-position']?: 'start' | 'end';
}

export const BannerCountdown: FC<BannerCountdownProps> = ({
  endsAt,
  foregroundColor,
  className,
  'data-testid': dataTestid = 'promotionBanner-countdown',
  'data-position': dataPosition,
}) => {
  const cd = useCountdown(endsAt);
  const [days, hours, minutes, seconds] = cd;

  if (cd.hideCountdown) {
    return null;
  }

  const tint = foregroundColor ?? COLORS.Black;
  const label = formatCountdownLabel(days, hours, minutes, seconds);

  return (
    <Pill
      className={className}
      data-testid={dataTestid}
      data-position={dataPosition}
      style={{color: tint}}
      aria-label={label}
    >
      <IconBox>
        <Icon
          asset={GenericIconName.Clock}
          width="100%"
          height="100%"
          fill={tint}
        />
      </IconBox>
      <Label>{label}</Label>
    </Pill>
  );
};

const formatCountdownLabel = (
  days: string,
  hours: string,
  minutes: string,
  seconds: string,
): string => {
  // Drop the days segment when it's "00" — matches the Flutter `alias` format.
  if (days === '00') {
    return `${hours}H ${minutes}M ${seconds}S`;
  }
  return `${days}D ${hours}H ${minutes}M ${seconds}S`;
};

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 17px;
  padding-inline: 4px 6px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  white-space: nowrap;

  ${bannerWideAndUpLinaria(`
    height: 22px;
    padding-inline: 4px 8px;
  `)}
`;

const IconBox = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 14px;
  height: 14px;

  ${bannerWideAndUpLinaria(`
    width: 18px;
    height: 18px;
  `)}
`;

const Label = styled.span`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 16px;
  font-variant-numeric: tabular-nums;
  color: inherit;

  ${bannerWideAndUpLinaria(`
    font-size: ${FONT_SIZES.px12};
    line-height: 18px;
  `)}
`;
